import type { AttributeDef, TaskStateProfile, TaskUI } from '@flexiplanner/types';

type PlanningActivity = Partial<TaskUI> & {
  customAttributes?: Record<string, unknown>;
  stateProfile?: Partial<TaskStateProfile> | null;
};

const REQUIRED_STATE_FIELDS: Array<[keyof TaskStateProfile, string]> = [
  ['required_productivity_energy', 'required P'],
  ['required_rejuvenation_energy', 'required RJ'],
  ['productivity_return', 'P return'],
  ['rejuvenation_return', 'RJ return'],
];

export function getMissingPlanningFields(
  activity: PlanningActivity,
  attributes: AttributeDef[] = []
): string[] {
  const missing: string[] = [];
  const customAttributes = activity.customAttributes || {};
  if (!String(activity.title || '').trim()) missing.push('title');
  if (!activity.bundleId) missing.push('bundle');
  if (!Number.isFinite(Number(activity.estimatedDuration)) || Number(activity.estimatedDuration) <= 0) {
    missing.push('duration');
  }

  for (const [key, label] of REQUIRED_STATE_FIELDS) {
    const value = activity.stateProfile?.[key];
    if (value === null || value === undefined || !Number.isFinite(Number(value))) {
      missing.push(label);
    }
  }

  for (const attr of attributes) {
    if (!isUserPlanningAttribute(attr)) continue;
    const value = getPlanningAttributeValue(customAttributes, attr);
    if (value === null || value === undefined || value === '') missing.push(attr.name);
  }

  return Array.from(new Set(missing));
}

export function isActivityPlanningComplete(
  activity: PlanningActivity,
  attributes: AttributeDef[] = []
): boolean {
  return getMissingPlanningFields(activity, attributes).length === 0;
}

export function formatMissingPlanningFields(missing: string[], limit = 5): string {
  if (missing.length === 0) return '';
  return `${missing.slice(0, limit).join(', ')}${missing.length > limit ? ` and ${missing.length - limit} more` : ''}`;
}

export function getPlanningAttributeValue(
  customAttributes: Record<string, unknown> | undefined,
  attr: AttributeDef
): unknown {
  const custom = customAttributes || {};
  const directValue = custom[attr.attr_id];
  if (directValue !== null && directValue !== undefined && directValue !== '') return directValue;

  const normalizedName = normalizeAttributeName(attr.name);
  if (normalizedName === 'emotion') {
    return custom.emotion_word || custom.emotion || custom.emotionWord || directValue;
  }

  return directValue;
}

export function isUserPlanningAttribute(attr: AttributeDef): boolean {
  if (!Boolean(attr.required)) return false;
  const normalizedName = normalizeAttributeName(attr.name);
  return ![
    'needs review',
    'review',
    'required fields complete',
    'missing required fields',
    'duration predicted',
    'duration prediction',
    'local title override',
    'local title',
    'source',
    'external source title',
  ].includes(normalizedName);
}

function normalizeAttributeName(value: unknown): string {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}
